import React from 'react';
import { TrendingUp, TrendingDown, Eye, MessageCircle, Phone, MapPin, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

// Simple line chart component
export function SimpleLineChart({ data, dataKey = 'views', height = 120 }) {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d[dataKey] - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div style={{ height: height }} className="w-full relative">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="rgb(16, 185, 129)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          points={`0,100 ${points} 100,100`}
          fill="url(#chartGradient)"
        />
        <polyline
          points={points}
          fill="none"
          stroke="rgb(16, 185, 129)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

// Stat card with change indicator
export function StatCard({ title, value, change, icon: Icon, subtitle }) {
  const isPositive = change >= 0;
  
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-sm">{title}</span>
          {Icon && <Icon className="w-5 h-5 text-slate-500" />}
        </div>
        <div className="flex items-end gap-3">
          <span className="text-3xl font-bold text-slate-50">{typeof value === 'number' ? value.toLocaleString() : value}</span>
          {change !== undefined && change !== null && (
            <span className={`flex items-center text-sm font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {isPositive ? '+' : ''}{change}%
            </span>
          )}
        </div>
        {subtitle && <span className="text-slate-500 text-sm">{subtitle}</span>}
      </CardContent>
    </Card>
  );
}

// Analytics dashboard for centers/professionals
export function ProfileAnalyticsDashboard({ analytics, onExport, entityType = 'center' }) {
  if (!analytics) return null;
  
  const { views, clicks, contacts, daily_views, alerts } = analytics;
  
  return (
    <div className="space-y-6">
      {/* Alert banner */}
      {alerts?.spike && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-emerald-400" />
          <div>
            <p className="font-medium text-emerald-400">¡Pico de visitas detectado!</p>
            <p className="text-sm text-slate-400">{alerts.spike_message}</p>
          </div>
        </div>
      )}
      
      {/* Overview stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Visitas hoy" 
          value={views?.today || 0} 
          icon={Eye}
        />
        <StatCard 
          title="Visitas este mes" 
          value={views?.month || 0} 
          change={views?.month_change_percent}
          subtitle="vs mes anterior"
        />
        <StatCard 
          title="Total visitas" 
          value={views?.total || 0}
          icon={Eye}
        />
        <StatCard 
          title="Contactos" 
          value={contacts?.total || 0}
          icon={MessageCircle}
          subtitle={`${contacts?.month || 0} este mes`}
        />
      </div>
      
      {/* Clicks breakdown */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-50">Interacciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <MessageCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-50">{clicks?.whatsapp || 0}</p>
              <p className="text-sm text-slate-400">WhatsApp</p>
            </div>
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <Phone className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-50">{clicks?.call || 0}</p>
              <p className="text-sm text-slate-400">Llamadas</p>
            </div>
            <div className="text-center p-4 bg-slate-800/50 rounded-xl">
              <MapPin className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-50">{clicks?.maps || 0}</p>
              <p className="text-sm text-slate-400">Google Maps</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Chart */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg text-slate-50">Visitas últimos 30 días</CardTitle>
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <Download className="w-4 h-4 mr-2" />Exportar CSV
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <SimpleLineChart data={daily_views} dataKey="views" height={150} />
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            <span>{daily_views?.[0]?.date}</span>
            <span>{daily_views?.[daily_views.length - 1]?.date}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Admin overview dashboard
export function AdminAnalyticsDashboard({ overview, topProfiles, trends, detailedStats, onExport, onLoadDetailed }) {
  const [activeTab, setActiveTab] = React.useState('overview');
  
  if (!overview) return null;
  
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-slate-200'}`}
        >
          Resumen
        </button>
        <button
          onClick={() => { setActiveTab('rankings'); if (onLoadDetailed) onLoadDetailed(); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'rankings' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-slate-200'}`}
        >
          Rankings Detallados
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'contacts' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-slate-200'}`}
        >
          Contactos por Entidad
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Overview stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard 
              title="Visitas hoy" 
              value={overview?.period_views?.today || 0} 
              icon={Eye}
            />
            <StatCard 
              title="Visitas mes" 
              value={overview?.period_views?.month || 0} 
              change={overview?.period_views?.month_change_percent}
            />
            <StatCard 
              title="Visitantes únicos" 
              value={overview?.overview?.unique_visitors_month || 0}
              subtitle="este mes"
            />
            <StatCard 
              title="Total contactos" 
              value={overview?.overview?.total_contacts || 0}
              icon={MessageCircle}
            />
            <StatCard 
              title="Total clics" 
              value={overview?.clicks?.total || 0}
              subtitle={`WA: ${overview?.clicks?.whatsapp || 0} | Tel: ${overview?.clicks?.call || 0}`}
            />
          </div>
          
          {/* Charts */}
          {trends?.trends && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-slate-50">Tendencias últimos 30 días</CardTitle>
                {onExport && (
                  <Button variant="outline" size="sm" onClick={onExport} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Download className="w-4 h-4 mr-2" />Exportar CSV
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <SimpleLineChart data={trends.trends} dataKey="views" height={180} />
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>{trends.trends[0]?.date}</span>
                  <span>{trends.trends[trends.trends.length - 1]?.date}</span>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Top profiles */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-slate-50">Top Centros</CardTitle>
              </CardHeader>
              <CardContent>
                {topProfiles?.top_centers?.length > 0 ? (
                  <div className="space-y-3">
                    {topProfiles.top_centers.map((center, idx) => (
                      <div key={center.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded">{idx + 1}</span>
                          <div>
                            <p className="font-medium text-slate-200">{center.name}</p>
                            <p className="text-sm text-slate-500">{center.city}</p>
                          </div>
                        </div>
                        <span className="text-slate-400">{center.views} visitas</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Sin datos aún</p>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-slate-50">Top Profesionales</CardTitle>
              </CardHeader>
              <CardContent>
                {topProfiles?.top_professionals?.length > 0 ? (
                  <div className="space-y-3">
                    {topProfiles.top_professionals.map((pro, idx) => (
                      <div key={pro.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded">{idx + 1}</span>
                          <div>
                            <p className="font-medium text-slate-200">{pro.name}</p>
                            <p className="text-sm text-slate-500">{pro.city}</p>
                          </div>
                        </div>
                        <span className="text-slate-400">{pro.views} visitas</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Sin datos aún</p>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-50">{overview?.overview?.total_centers || 0}</p>
                <p className="text-sm text-slate-400">Centros registrados</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-50">{overview?.overview?.total_professionals || 0}</p>
                <p className="text-sm text-slate-400">Profesionales</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-50">{overview?.views_by_type?.centers || 0}</p>
                <p className="text-sm text-slate-400">Visitas a centros</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-50">{overview?.views_by_type?.professionals || 0}</p>
                <p className="text-sm text-slate-400">Visitas a profesionales</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {activeTab === 'rankings' && detailedStats && (
        <DetailedRankings stats={detailedStats} />
      )}

      {activeTab === 'contacts' && detailedStats && (
        <ContactsBreakdown stats={detailedStats} />
      )}

      {(activeTab === 'rankings' || activeTab === 'contacts') && !detailedStats && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      )}
    </div>
  );
}

// Detailed rankings component
function DetailedRankings({ stats }) {
  if (!stats) return null;
  
  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Visitas totales" value={stats.summary?.total_views || 0} change={stats.summary?.views_change} icon={Eye} />
        <StatCard title="Visitantes únicos" value={stats.summary?.unique_visitors || 0} change={stats.summary?.unique_visitors_change} />
        <StatCard title="WhatsApp clicks" value={stats.summary?.whatsapp_clicks || 0} change={stats.summary?.whatsapp_change} icon={MessageCircle} />
        <StatCard title="Llamadas" value={stats.summary?.call_clicks || 0} icon={Phone} />
      </div>

      {/* Devices */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-50">Dispositivos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            <div className="flex-1 p-4 bg-slate-800/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.devices?.mobile || 0}</p>
              <p className="text-sm text-slate-400">Móvil</p>
              <p className="text-xs text-slate-500">{stats.devices?.mobile && stats.summary?.total_views ? Math.round((stats.devices.mobile / stats.summary.total_views) * 100) : 0}%</p>
            </div>
            <div className="flex-1 p-4 bg-slate-800/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.devices?.desktop || 0}</p>
              <p className="text-sm text-slate-400">Desktop</p>
              <p className="text-xs text-slate-500">{stats.devices?.desktop && stats.summary?.total_views ? Math.round((stats.devices.desktop / stats.summary.total_views) * 100) : 0}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Regions */}
      {stats.top_regions?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">Regiones con más búsquedas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {stats.top_regions.map((r, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-lg font-bold text-slate-200">{r.views}</p>
                  <p className="text-xs text-slate-400 truncate">{r.region}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Pages */}
      {stats.top_pages?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">Páginas más visitadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_pages.slice(0, 10).map((page, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                  <span className="text-sm text-slate-300 truncate flex-1">{page.path}</span>
                  <span className="text-sm text-slate-400 ml-4">{page.views}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Centers by Views */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">🏥 Top 15 Centros (Visitas)</CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {stats.centers?.most_viewed?.length > 0 ? (
              <div className="space-y-2">
                {stats.centers.most_viewed.map((c, idx) => (
                  <div key={c.id} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg">
                    <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-200 text-sm truncate">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.city} {c.is_premium && '⭐'}</p>
                    </div>
                    <span className="text-emerald-400 font-medium">{c.views}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">👨‍⚕️ Top 15 Profesionales (Visitas)</CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {stats.professionals?.most_viewed?.length > 0 ? (
              <div className="space-y-2">
                {stats.professionals.most_viewed.map((p, idx) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg">
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-500/20 text-blue-400 text-xs font-bold rounded">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-200 text-sm truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.specialty || p.city}</p>
                    </div>
                    <span className="text-blue-400 font-medium">{p.views}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* Top Workshops */}
      {stats.workshops?.most_viewed?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">🎨 Top 15 Talleres (Visitas)</CardTitle>
          </CardHeader>
          <CardContent className="max-h-72 overflow-y-auto">
            <div className="grid md:grid-cols-2 gap-2">
              {stats.workshops.most_viewed.map((w, idx) => (
                <div key={w.id} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg">
                  <span className="w-6 h-6 flex items-center justify-center bg-purple-500/20 text-purple-400 text-xs font-bold rounded">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-200 text-sm truncate">{w.name}</p>
                    <p className="text-xs text-slate-500">{w.city}</p>
                  </div>
                  <span className="text-purple-400 font-medium">{w.views}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Contacts breakdown component - shows who got contacted
function ContactsBreakdown({ stats }) {
  if (!stats) return null;
  
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Contactos" value={stats.summary?.total_contacts || 0} icon={MessageCircle} />
        <StatCard title="WhatsApp" value={stats.summary?.whatsapp_clicks || 0} icon={MessageCircle} />
        <StatCard title="Llamadas" value={stats.summary?.call_clicks || 0} icon={Phone} />
        <StatCard title="Formularios" value={stats.summary?.form_submissions || 0} />
      </div>

      {/* Centers that received contacts */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-50">🏥 Centros que recibieron contactos</CardTitle>
          <p className="text-sm text-slate-400">Desglose por WhatsApp, llamadas y formularios</p>
        </CardHeader>
        <CardContent>
          {stats.centers?.most_contacted?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="pb-3 pr-4">#</th>
                    <th className="pb-3 pr-4">Centro</th>
                    <th className="pb-3 pr-4">Ciudad</th>
                    <th className="pb-3 pr-4 text-center">WhatsApp</th>
                    <th className="pb-3 pr-4 text-center">Llamadas</th>
                    <th className="pb-3 pr-4 text-center">Formularios</th>
                    <th className="pb-3 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.centers.most_contacted.map((c, idx) => (
                    <tr key={c.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-3 pr-4 text-slate-400">{idx + 1}</td>
                      <td className="py-3 pr-4">
                        <span className="text-slate-200">{c.name}</span>
                        {c.is_premium && <span className="ml-2 text-yellow-400">⭐</span>}
                      </td>
                      <td className="py-3 pr-4 text-slate-400">{c.city}</td>
                      <td className="py-3 pr-4 text-center text-emerald-400">{c.whatsapp || 0}</td>
                      <td className="py-3 pr-4 text-center text-blue-400">{c.calls || 0}</td>
                      <td className="py-3 pr-4 text-center text-purple-400">{c.forms || 0}</td>
                      <td className="py-3 text-center font-bold text-slate-200">{c.total_contacts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-slate-500 text-center py-8">No hay datos de contactos para centros</p>}
        </CardContent>
      </Card>

      {/* Professionals that received contacts */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-50">👨‍⚕️ Profesionales que recibieron contactos</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.professionals?.most_contacted?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="pb-3 pr-4">#</th>
                    <th className="pb-3 pr-4">Profesional</th>
                    <th className="pb-3 pr-4">Especialidad</th>
                    <th className="pb-3 pr-4 text-center">WhatsApp</th>
                    <th className="pb-3 pr-4 text-center">Llamadas</th>
                    <th className="pb-3 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.professionals.most_contacted.map((p, idx) => (
                    <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-3 pr-4 text-slate-400">{idx + 1}</td>
                      <td className="py-3 pr-4 text-slate-200">{p.name}</td>
                      <td className="py-3 pr-4 text-slate-400">{p.specialty || '-'}</td>
                      <td className="py-3 pr-4 text-center text-emerald-400">{p.whatsapp || 0}</td>
                      <td className="py-3 pr-4 text-center text-blue-400">{p.calls || 0}</td>
                      <td className="py-3 text-center font-bold text-slate-200">{p.total_contacts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-slate-500 text-center py-8">No hay datos de contactos para profesionales</p>}
        </CardContent>
      </Card>

      {/* Workshops that received contacts */}
      {stats.workshops?.most_contacted?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-slate-50">🎨 Talleres que recibieron contactos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="pb-3 pr-4">#</th>
                    <th className="pb-3 pr-4">Taller</th>
                    <th className="pb-3 pr-4">Ciudad</th>
                    <th className="pb-3 pr-4 text-center">WhatsApp</th>
                    <th className="pb-3 pr-4 text-center">Llamadas</th>
                    <th className="pb-3 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.workshops.most_contacted.map((w, idx) => (
                    <tr key={w.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-3 pr-4 text-slate-400">{idx + 1}</td>
                      <td className="py-3 pr-4 text-slate-200">{w.name}</td>
                      <td className="py-3 pr-4 text-slate-400">{w.city || '-'}</td>
                      <td className="py-3 pr-4 text-center text-emerald-400">{w.whatsapp || 0}</td>
                      <td className="py-3 pr-4 text-center text-blue-400">{w.calls || 0}</td>
                      <td className="py-3 text-center font-bold text-slate-200">{w.total_contacts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
